import { Box, Text }                          from '@chakra-ui/react';
import { HiBan, HiCheck, HiOutlineClock,
         HiOutlineMinusSm }                   from "react-icons/hi";
import { Link, useNavigate }                  from 'react-router-dom';
import Image                                  from '@/components/Image/Image';
import { useTranslation }                     from 'react-i18next';
import ActionsButton                          from '@/components/actionsButton/ActionsButton';
import { Tag }                                from "@/components/ui/tag";
import i18n                                   from '@/i18n.config';
import WebsiteEntertainmentModel              from '@/models/services/website/WebsiteEntertainmentModel';
import links                                  from '@/tools/links';
import useTranslations                        from '@/hooks/useTranslations';
import { StandListBoxRowData }                from '@/pages/Stands/components/StandsListBox';

interface IStandListRowProps {
	rowData: StandListBoxRowData;
	websiteEntertainment: WebsiteEntertainmentModel;
}

const STATUS_ICON: Partial<Record<Website_StandPageValidationStatusReference, React.ReactNode>> = {
	DISABLED:             <HiOutlineMinusSm />,
	IN_CREATION:          <HiOutlineClock />,
	REFUSED:              <HiBan />,
	VALIDATED:            <HiCheck />,
	VALIDATION_REQUESTED: <HiOutlineClock />,
};

const StandsListRow = (props: IStandListRowProps) => {
	const { rowData, websiteEntertainment } = props;
	const { stand, standPage, standPageValidationStatus } = rowData;

	const { t } = useTranslation();
	const navigate = useNavigate();
	const { getNavigationTranslation } = useTranslations();

	const getStandUrl = (): string => {
		if (!websiteEntertainment.website?.url) return '';
		return `${websiteEntertainment.website.url}/${links.stand(i18n.language, websiteEntertainment.slugPrefix, stand.slug)}`;
	};

	const standUrl = getStandUrl();
	const standPageImages = standPage?.getFiles(['stand_page_image']).slice(0, 3);

	return (
		<Box display="flex" alignItems="center" justifyContent="space-between">
			<Link to={`${getNavigationTranslation('editStand', 'path')}/${stand.id}`} style={{ width: '100%' }}>
				<Box display="flex" justifyContent="space-between" paddingRight="20px">
					<Box display="flex" alignItems="stretch" gap="25px">
						<Box minWidth="75px" maxWidth="75px" height="75px" overflow="hidden" borderRadius="md">
							<Image
								errorSize='xl'
								src={standPage?.getFiles('stand_page_logo').at(0)?.url}
								width="75px"
								height="75px"
								objectFit="cover"
							/>
						</Box>
						<Box display="flex" flexDirection="column" height="100%" paddingRight="25px">
							<Text
								className="poppins-semibold"
								fontSize="16px"
								textDecoration="underline"
								style={{ fontStyle: stand.title ? 'unset' : 'italic' }}
							>
								{stand.title || t('pages.stands.list.noTitle')}
							</Text>
							<Box fontSize="12px">
								<span style={{ fontStyle: stand.hall ? 'unset' : 'italic' }}>
									{stand.hall ? <span>{t('pages.stands.list.hall')} : {stand.hall}</span> : t('pages.stands.list.noHall')}
								</span>
								&nbsp;-&nbsp;
								<span style={{ fontStyle: stand.slot ? 'unset' : 'italic' }}>
									{stand.slot ? <span>{t('pages.stands.list.slot')} : {stand.slot}</span> : t('pages.stands.list.noSlot')}
								</span>
							</Box>
							{standPage && standPageValidationStatus && (
								<Tag
									colorPalette={standPageValidationStatus.color}
									startElement={STATUS_ICON[standPageValidationStatus.reference]}
									size="md"
									width="fit-content"
									marginTop="10px"
								>
									<Text textWrap="wrap">{standPageValidationStatus.getLabel()}</Text>
								</Tag>
							)}
						</Box>
					</Box>
					{standPageImages && standPageImages.length > 0 && (
						<Box display="flex" flexWrap="wrap" gap="10px">
							{standPageImages.map(spi => (
								<Box key={spi.url} minWidth="75px" maxWidth="75px" height="75px" overflow="hidden" borderRadius="md">
									<Image errorSize='xl' src={spi.url} width="75px" height="75px" objectFit="cover" />
								</Box>
							))}
						</Box>
					)}
				</Box>
			</Link>
			<ActionsButton actions={[
				{
					label: t('pages.stands.actions.edit.label'),
					do: () => navigate(`${getNavigationTranslation('editStand', 'path')}/${stand.id}`),
				},
				{
					label: t('pages.stands.actions.viewOnline.label'),
					disabled: !standUrl,
					do: () => window.open(standUrl, '_blank'),
				},
			]} />
		</Box>
	);
};

export default StandsListRow;
